/* eslint-disable react/no-danger */
import { numberToWords } from '@persian-tools/persian-tools';
import cs from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@/DesignSystem/Component';
import { authSetIsTransitioningAction } from '@/Module/Auth/Store/Slice/AuthSlice';
import { FILE_BROWSER_URL } from '@/Module/Core/Constant';
import { htmlStringNormalizer, secondToMS } from '@/Module/Core/Helper';
import type { IBaseComponentProps, IEntity } from '@/Module/Core/Model/DataModel';
import { categoryLessonSectionListCategorySelector } from '@/Module/HomePage/Store/Selector/categoryLessonSectionListCategorySelector';
import { IconClock, IconPlay } from '@/Module/Icon';
import { prepareLessonSectionUrl } from '@/Module/Lesson/Helper';
import type { ILesson } from '@/Module/Lesson/Model/DataModel';
import { lessonSectionSelectById, relatedLessonSectionSelectById } from '@/Module/Lesson/Store/Selector';
import { useAppSelector } from '@/Module/Store/Hook';
import { lessonSelectByTeacherId } from '@/Module/Teacher/Store/Selector';
interface ILessonProps extends IBaseComponentProps {
  id: ILesson['id'];
  index: number;
  isRelated?: boolean;
  isSummary?: boolean;
  isTeacher?: boolean;
  isRandom?: boolean;
  categoryCategoryId?: IEntity['id'];
  imageSize?: number;
}
const LessonSection: FC<ILessonProps> = ({
  id,
  className,
  index,
  isRelated,
  isSummary,
  categoryCategoryId,
  isRandom,
  isTeacher,
  imageSize
}) => {
  const dispatch = useDispatch();
  const entity = useAppSelector(state => {
    if (isRelated) return relatedLessonSectionSelectById(state, id);
    if (isTeacher) return lessonSelectByTeacherId(state, id);
    if (isRandom && categoryCategoryId && id) return categoryLessonSectionListCategorySelector(state, categoryCategoryId, id);
    return lessonSectionSelectById(state, id);
  });
  const lessonTitle = useMemo(() => {
    if (!index && !entity?.sortOrder) return '';
    try {
      return numberToWords(entity?.sortOrder ?? index, {
        ordinal: true
      }) as string;
    } catch {
      return '';
    }
  }, [index, entity?.sortOrder]);
  const isComingSoon = useMemo(() => {
    if (!entity) return true;
    return !entity.mediaLink && !entity.mediaLink2;
  }, [entity]);
  const pictureUrl = useMemo(() => {
    if (!entity || isComingSoon) return `${FILE_BROWSER_URL}/img/img-lesson-section-coming-soon.webp`;
    if (entity.pictureUrl?.length > 10) return entity.pictureUrl;
    return `${FILE_BROWSER_URL}/img/img-lesson-section-default.webp`;
  }, [entity, isComingSoon]);
  if (!entity) return null;
  const handleClick = async () => {
    dispatch(authSetIsTransitioningAction(true));
  };
  const Component = !isComingSoon ? Link : 'div';
  const componentProps = {
    ...(!isComingSoon ? {
      onClick: handleClick,
      href: prepareLessonSectionUrl({
        ...entity
      })
    } : {})
  } as any;
  const imageWidth = imageSize ?? 346;
  const imageHeight = imageWidth / 1.783;
  return <div dir="rtl" className={cs('group', className)} data-sentry-component="LessonSection" data-sentry-source-file="LessonSection.tsx">
      <Component {...componentProps} data-sentry-element="Component" data-sentry-source-file="LessonSection.tsx">
        <Box color="lightOrange" className="relative mb-5 overflow-hidden p-1 group-hover:shadow-themeHover group-hover:shadow-orangeFour" data-sentry-element="Box" data-sentry-source-file="LessonSection.tsx">
          <Image width={imageWidth} height={imageHeight} className="z-0 w-full" src={pictureUrl} alt={entity.name} data-sentry-element="Image" data-sentry-source-file="LessonSection.tsx" />
          <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-[rgba(69,41,4,.52)] transition-all group-hover:bg-transparent">
            <IconPlay className="h-10 w-10 rounded-full bg-white/50 text-white transition-all group-hover:scale-110 group-hover:bg-orangeFour" data-sentry-element="IconPlay" data-sentry-source-file="LessonSection.tsx" />
          </div>

          {!!entity.duration && <span className="absolute bottom-0 right-0 z-20 inline-flex h-8 items-center gap-1 bg-orangeOne pl-4 pr-1 text-orangeFive">
              <IconClock size="small" />
              <span>{secondToMS(entity.duration)}</span>
            </span>}
        </Box>

        <span className="mb-1 font-bold text-orangeFive group-hover:text-orangeSix lg:text-lg">
          <span className="ml-1 inline text-base font-normal">قسمت {lessonTitle}:</span>
          <span className="inline text-base">{entity.name}</span>
        </span>

        {entity.summary && isSummary && <div className="line-clamp-2 text-sm text-blackFive lg:text-base" dangerouslySetInnerHTML={{
        __html: htmlStringNormalizer(entity.summary)
      }} />}
      </Component>
    </div>;
};
LessonSection.defaultProps = {
  imageSize: 346
};
export default LessonSection;